/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import classNames from 'classnames';
import { yupResolver } from '@hookform/resolvers';
import Select from 'react-select';

import { usersCleanUp } from 'state/actions/users';
import { useFormatMessage } from 'hooks';
import { genders } from 'utils';
import ErrorMessage from 'components/ErrorMessage';

import './UserForm.scss';

const UserForm = ({ user, onSubmitHandler, schema }) => {
  const { loading, success, id } = useSelector(
    (state) => ({
      id: state.auth.userData.id,
      loading: state.users.loading,
      success: state.users.success,
    }),
    shallowEqual
  );

  const isEditing = user.id?.length > 0;
  const isProfile = user.id === id;

  const dispatch = useDispatch();

  const history = useHistory();

  const { register, handleSubmit, errors, control, watch, setValue } = useForm({
    defaultValues: { ...user, gender: { label: 'Male', value: 'Male' } },
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (success) {
      setValue('file', null);
    }
    return () => dispatch(usersCleanUp());
  }, [dispatch, success, setValue]);

  const invalidEmailMessage = useFormatMessage('UserForm.invalidEmail');

  const invalidPhoneMessage = useFormatMessage('UserForm.invalidPhone');

  const imagePreviewUrl =
    watch('file') && watch('file')[0]
      ? URL.createObjectURL(watch('file')[0])
      : user.imageUrl;

  const goBackMessage = useFormatMessage('UserForm.goBack');

  const pickAnotherFileMessage = useFormatMessage('UserForm.pickAnotherFile');

  const pickFileMessage = useFormatMessage('UserForm.pickFile');

  const emailMessage = useFormatMessage('UserForm.email');

  const phoneMessage = useFormatMessage('UserForm.phone');

  const genderMessage = useFormatMessage('UserForm.gender');

  const zipCodeErrorMessage = useFormatMessage('UserForm.zipCodeError');

  const ageErrorMessage = useFormatMessage('UserForm.ageError');

  return (
    <>
      <div className="tile is-ancestor">
        <div className="tile is-parent">
          <div className="card tile is-child">
            <header className="card-header">
              <p className="card-header-title">
                <span className="icon">
                  <i className="mdi mdi-account-edit default" />
                </span>
                {useFormatMessage('UserForm.userInfo')}
              </p>
            </header>
            <div className="card-content">
              <form onSubmit={handleSubmit(onSubmitHandler)}>
                {isEditing ? (
                  <div className="field is-horizontal">
                    <div className="field-label is-normal">
                      <label className="label">{emailMessage}</label>
                    </div>
                    <div className="field-body">
                      <div className="field">
                        <div className="control">
                          <input
                            type="text"
                            readOnly="readOnly"
                            className="input is-static"
                            name="email"
                            ref={register}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="field is-horizontal">
                      <div className="field-label is-normal">
                        <label className="label">{emailMessage}</label>
                      </div>
                      <div className="field-body">
                        <div className="field">
                          <div className="control">
                            <input
                              className={classNames(`input`, {
                                'is-danger': errors.email,
                              })}
                              ref={register}
                              name="email"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    {errors.email && (
                      <div className="field is-horizontal">
                        <div className="field-label is-normal" />
                        <div className="field-body">
                          <ErrorMessage text={invalidEmailMessage} />
                        </div>
                      </div>
                    )}
                  </>
                )}
                {isEditing ? (
                  <div className="field is-horizontal">
                    <div className="field-label is-normal">
                      <label className="label">{phoneMessage}</label>
                    </div>
                    <div className="field-body">
                      <div className="field">
                        <div className="control">
                          <input
                            type="tel"
                            readOnly="readOnly"
                            className="input is-static"
                            name="phone"
                            ref={register}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="field is-horizontal">
                      <div className="field-label is-normal">
                        <label className="label">{phoneMessage}</label>
                      </div>
                      <div className="field-body">
                        <div className="field">
                          <div className="control">
                            <input
                              className={classNames(`input`, {
                                'is-danger': errors.phone,
                              })}
                              ref={register}
                              name="phone"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    {errors.phone && (
                      <div className="field is-horizontal">
                        <div className="field-label is-normal" />
                        <div className="field-body">
                          <ErrorMessage text={invalidPhoneMessage} />
                        </div>
                      </div>
                    )}
                  </>
                )}
                <div className="field is-horizontal">
                  <div className="field-label is-normal">
                    <label className="label">
                      {useFormatMessage('UserForm.name')}
                    </label>
                  </div>
                  <div className="field-body">
                    <div className="field">
                      <div className="control">
                        <input
                          name="name"
                          id="name"
                          className={classNames('input', {
                            'is-danger': errors.name,
                          })}
                          ref={register}
                          type="text"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                {errors.name && (
                  <div className="field is-horizontal">
                    <div className="field-label is-normal" />
                    <div className="field-body">
                      <ErrorMessage />
                    </div>
                  </div>
                )}
                <div className="field is-horizontal">
                  <div className="field-label is-normal">
                    <label className="label">
                      {useFormatMessage('UserForm.gender')}
                    </label>
                  </div>
                  <div className="field-body">
                    <div className="field">
                      <div className="control">
                        <Controller
                          control={control}
                          name="gender"
                          render={({ onChange, name, value }) => (
                            <Select
                              options={genders}
                              name={name}
                              ref={register}
                              placeholder={genderMessage}
                              onChange={onChange}
                              value={value}
                            />
                          )}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="field is-horizontal">
                  <div className="field-label is-normal">
                    <label className="label">
                      {useFormatMessage('UserForm.age')}
                    </label>
                  </div>
                  <div className="field-body">
                    <div className="field">
                      <div className="control">
                        <input
                          name="age"
                          id="age"
                          className="input"
                          ref={register}
                          type="tel"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                {errors.age && (
                  <div className="field is-horizontal">
                    <div className="field-label is-normal" />
                    <div className="field-body">
                      <ErrorMessage text={ageErrorMessage} />
                    </div>
                  </div>
                )}
                <div className="field is-horizontal">
                  <div className="field-label is-normal">
                    <label className="label">
                      {useFormatMessage('UserForm.zipCode')}
                    </label>
                  </div>
                  <div className="field-body">
                    <div className="field">
                      <div className="control">
                        <input
                          name="zipCode"
                          id="zipCode"
                          className="input"
                          ref={register}
                          type="tel"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                {errors.zipCode && (
                  <div className="field is-horizontal">
                    <div className="field-label is-normal" />
                    <div className="field-body">
                      <ErrorMessage text={zipCodeErrorMessage} />
                    </div>
                  </div>
                )}

                <hr />

                <div className="field is-horizontal">
                  <div className="field-label is-normal">
                    <label className="label">
                      {useFormatMessage('UserForm.logo')}
                    </label>
                  </div>
                  <div className="field-body">
                    <div className="field">
                      <div className="file has-name">
                        <label className="file-label">
                          <input
                            className="file-input"
                            type="file"
                            name="file"
                            ref={register}
                            accept="image/*"
                          />
                          <span className="file-cta">
                            <span className="file-icon">
                              <i className="mdi mdi-upload" />
                            </span>
                            <span className="file-label">
                              {watch('file') && watch('file').file
                                ? pickAnotherFileMessage
                                : pickFileMessage}
                            </span>
                          </span>
                          <span className="file-name">
                            {watch('file') && watch('file')[0]?.name}
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <hr />
                <div className="field is-horizontal">
                  <div className="field-label" />
                  <div className="field-body">
                    <div className="field">
                      <div className="field is-grouped">
                        <div className="control">
                          <button
                            type="submit"
                            className={`button is-primary ${
                              loading && 'is-loading'
                            }`}
                          >
                            <span>{useFormatMessage('UserForm.submit')}</span>
                          </button>
                        </div>
                        {!isProfile && (
                          <div
                            onClick={() => history.goBack()}
                            className="button"
                          >
                            {goBackMessage}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
        <div className="tile is-parent preview">
          <div className="card tile is-child">
            <header className="card-header">
              <p className="card-header-title">
                <span className="icon">
                  <i className="mdi mdi-account default" />
                </span>
                {useFormatMessage('UserForm.userPreview')}
              </p>
            </header>
            <div className="card-content">
              {imagePreviewUrl && (
                <>
                  <div className="is-user-avatar image has-max-width is-aligned-center">
                    <img
                      className="user-avatar"
                      src={imagePreviewUrl}
                      alt="User profile logo preview"
                    />
                  </div>
                  <hr />
                </>
              )}

              {!isEditing && (
                <>
                  <div className="field">
                    <label className="label">{emailMessage}</label>
                    <div className="control is-clearfix">
                      <input
                        data-testid="email"
                        type="text"
                        readOnly="readOnly"
                        className="input is-static"
                        value={watch('email')}
                      />
                    </div>
                  </div>
                  <div className="field">
                    <label className="label">{phoneMessage}</label>
                    <div className="control is-clearfix">
                      <input
                        data-testid="phone"
                        type="text"
                        readOnly="readOnly"
                        className="input is-static"
                        value={watch('phone')}
                      />
                    </div>
                  </div>
                </>
              )}

              <div className="field">
                <label className="label">
                  {useFormatMessage('UserForm.name')}
                </label>
                <div className="control is-clearfix">
                  <input
                    data-testid="name"
                    type="text"
                    readOnly="readOnly"
                    className="input is-static"
                    value={watch('name')}
                  />
                </div>
              </div>
              <div className="field">
                <label className="label">{phoneMessage}</label>
                <div className="control is-clearfix">
                  <input
                    data-testid="phone"
                    type="text"
                    readOnly="readOnly"
                    className="input is-static"
                    value={watch('phone')}
                  />
                </div>
              </div>
              <div className="field">
                <label className="label">
                  {useFormatMessage('UserForm.gender')}
                </label>
                <div className="control is-clearfix">
                  <input
                    data-testid="gender"
                    type="text"
                    readOnly="readOnly"
                    className="input is-static"
                    value={watch('gender')?.value}
                  />
                </div>
              </div>
              <div className="field">
                <label className="label">
                  {useFormatMessage('UserForm.age')}
                </label>
                <div className="control is-clearfix">
                  <input
                    data-testid="age"
                    type="text"
                    readOnly="readOnly"
                    className="input is-static"
                    value={watch('age')}
                  />
                </div>
              </div>
              <div className="field">
                <label className="label">
                  {useFormatMessage('UserForm.zipCode')}
                </label>
                <div className="control is-clearfix">
                  <input
                    data-testid="zipCode"
                    type="text"
                    readOnly="readOnly"
                    className="input is-static"
                    value={watch('zipCode')}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

UserForm.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string.isRequired,
    phone: PropTypes.string,
    age: PropTypes.number,
    gender: PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    }),
    zipCode: PropTypes.number,
    imageUrl: PropTypes.string,
    email: PropTypes.string.isRequired,
  }).isRequired,
  onSubmitHandler: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  schema: PropTypes.object.isRequired,
};

export default UserForm;
